import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, NgZone, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { AuthenticationService } from '@app/core/authentication/authentication.service';
import {
  ApiPropertyRuleset,
  ApiSimplePropertyRuleset,
  PropertyRuleset,
  RuleSetDialogAction,
  RuleSetDialogMode,
} from '@app/features/property-rulesets/property-ruleset.interface';
import { RulesetFormComponent } from '@app/features/property-rulesets/ruleset-form/ruleset-form.component';
import { RulesetSearchComponent } from '@app/features/property-rulesets/ruleset-search/ruleset-search.component';
import { RuleSetsOptInModalComponent } from '@app/modules/calendar/components/rule-sets-opt-in-modal/rule-sets-opt-in-modal.component';
import { Property, SegmentEvent } from '@app/shared/interfaces';
import { EventsService } from '@app/shared/services/events/events.service';
import { PosthogService } from '@app/shared/services/posthog/posthog.service';
import { SegmentIoService } from '@app/shared/services/segmentIo/segment-io.service';
import { environment } from '@env/environment';
import { Logger } from '@utils';
import { eachDay, format } from 'date-fns';
import {
  BehaviorSubject,
  Observable,
  Subject,
  combineLatest,
  debounceTime,
  filter,
  firstValueFrom,
  map,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { RulesetDeletePromptComponent } from '@app/features/pricing-and-availability/components/ruleset-delete-prompt/ruleset-delete-prompt.component';
import { UserPermissionsService } from '@app/core/user-permissions/user-permissions.service';

@Injectable({
  providedIn: 'root',
})
export class PropertyRulesetService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl;
  private dialog = inject(MatDialog);
  private eventsService = inject(EventsService);
  private authenticationService = inject(AuthenticationService);
  private userPermissionsService = inject(UserPermissionsService);
  private zone = inject(NgZone);
  private posthog = inject(PosthogService);
  private segmentIoService = inject(SegmentIoService);


  calendarDaysUpdated$ = new Subject<{ propertyId: Property['id']; startDate: string; endDate: string }>();
  rulesetsUpdated$ = new Subject<void>();

  rulesets$: BehaviorSubject<ApiSimplePropertyRuleset[]> = new BehaviorSubject<ApiSimplePropertyRuleset[]>([]);

  private optInDismissedAuth$ = this.authenticationService.user$.pipe(
    map((user) => user?.calendar_rulesets_opt_in_dismissed ?? false)
  );
  private enabledAuth$ = this.authenticationService.user$.pipe(map((user) => user?.calendar_rulesets_enabled ?? false));

  private _optInDismissed$ = new BehaviorSubject<boolean>(false);
  private _enabled$ = new BehaviorSubject<boolean>(false);

  public hasDismissedOptIn$ = combineLatest([this.optInDismissedAuth$, this._optInDismissed$]).pipe(
    map(([hasDismissedOptInAuth, _optInDismissed]) => hasDismissedOptInAuth || _optInDismissed)
  );

  // determines if the user has selected hospitable to be the source of truth regarding rulesets
  public enabled$ = combineLatest([this.enabledAuth$, this._enabled$]).pipe(
    map(([enabledAuth, _enabled]) => enabledAuth || _enabled)
  );

  public canViewCalendarRulesets$ = this.userPermissionsService.hasCalendarReadPricing$;

  public canManageCalendarRulesets$ = combineLatest([
    this.userPermissionsService.hasCalendarUpdatePricingAndAvailability$,
    this.userPermissionsService.hasCalendarUpdateAvailability$,
  ]).pipe(map(([hasCalendarUpdatePricingAndAvailability, hasCalendarUpdateAvailability]) => hasCalendarUpdatePricingAndAvailability && hasCalendarUpdateAvailability));

  constructor() {
    this.rulesetsUpdated$.pipe(
      debounceTime(200),
      takeUntilDestroyed()).subscribe(() => {
      this.getCalendarRulesets().pipe(take(1)).subscribe();
    });
  }

  public checkForOptIn(): Observable<false | ApiSimplePropertyRuleset[]> {
    return this.hasDismissedOptIn$.pipe(
      switchMap((hasDismissedOptIn): Observable<false | ApiSimplePropertyRuleset[]> => {
        if (hasDismissedOptIn) {
          return of(false as const);
        }

        return this.getCalendarRulesets('airbnb-official').pipe(
          map((rulesets): false | ApiSimplePropertyRuleset[] => (rulesets.length > 0 ? rulesets : false))
        );
      })
    );
  }

  getCalendarRulesets(source?: 'airbnb-official' | 'hospitable', sort?: 'recency'): Observable<ApiSimplePropertyRuleset[]> {
    let params = new HttpParams();

    if (sort) {
      params = params.set('sort', sort);
    }

    return this.http.get<{ data: ApiSimplePropertyRuleset[] }>(`${this.apiUrl}/calendar-rulesets`, { params }).pipe(
      map((response) => response.data?.sort((a, b) => (a.title ?? '').localeCompare(b.title ?? ''))),
      tap((rulesets) => this.rulesets$.next(rulesets)),
      map((rulesets) => (source ? rulesets.filter((ruleset) => ruleset.source === source) : rulesets)),
    );
  }

  getCalendarRuleset(uuid: string): Observable<PropertyRuleset> {
    // return of(this.mapApiPropertyRulesetToPropertyRuleset(this.TEST_API_RESPONSE));

    return this.http.get<{ data: ApiPropertyRuleset }>(`${this.apiUrl}/calendar-rulesets/${uuid}`).pipe(
      map((response) => response.data),
      map(this.mapApiPropertyRulesetToPropertyRuleset)
    );
  }

  createCalendarRuleset(ruleset: PropertyRuleset): Observable<unknown> {
    return this.http.post<ApiPropertyRuleset>(
      `${this.apiUrl}/calendar-rulesets`,
      this.mapPropertyRulesetToApiPropertyRuleset(ruleset)
    );
  }

  updateCalendarRuleset(uuid: string, ruleset: PropertyRuleset): Observable<unknown> {
    return this.http.put<ApiPropertyRuleset>(
      `${this.apiUrl}/calendar-rulesets/${uuid}`,
      this.mapPropertyRulesetToApiPropertyRuleset(ruleset)
    );
  }

  deleteCalendarRuleset(uuid: string): Observable<unknown> {
    return this.http.delete(`${this.apiUrl}/calendar-rulesets/${uuid}`);
  }

  enableRulesetLayer(): void {
    this.eventsService.fire('calendar-ruleset-layer-enabled', true);
  }

  dismissOptIn(): Observable<unknown> {
    const dismissed = true;
    return this.http
      .post(`${this.apiUrl}/calendar-rulesets/set-opt-in-dismissed-status`, {
        dismissed,
      })
      .pipe(
        tap(() => {
          this._optInDismissed$.next(dismissed);
        })
      );
  }

  optIn(imports_initial_rulesets: boolean): Observable<unknown> {
    return this.http
      .post(`${this.apiUrl}/calendar-rulesets/opt-in`, {
        imports_initial_rulesets,
      })
      .pipe(
        tap(() => {
          this._enabled$.next(true);
        })
      );
  }

  changeOrRemoveCalendarRulesetFromDays(
    propertyIds: Property['id'][],
    ruleset: Pick<ApiSimplePropertyRuleset, 'id' | 'color'>,
    startDate: string,
    endDate: string,
    daysOfTheWeek?: number[]
  ) {
    startDate = this.formatDateOrError(startDate);
    endDate = this.formatDateOrError(endDate);
    const dates = eachDay(startDate, endDate);

    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(startDate) || !dateRegex.test(endDate)) {
      Logger.error(
        '[PropertyRulesetService] (changeOrRemoveCalendarRulesetFromDays) Invalid date format. Dates must be in YYYY-MM-DD format'
      );
      return;
    }

    const payload = {
      start: startDate,
      end: endDate,
      calendar_ruleset_id: ruleset?.id || null,
    };

    if (daysOfTheWeek?.length) {
      payload['days_of_the_week'] = daysOfTheWeek;
    }

    propertyIds.forEach((propertyId) => {
      // TODO: Error handling here

      this.eventsService.fire('calendar-ruleset-optimistic-update', {
        propertyId,
        dates,
        desiredPrice: null,
        desiredAvailability: null,
        desiredLengthOfStay: null,
        desiredNote: null,
        desiredClosedForCheckin: null,
        desiredClosedForChekout: null,
        desiredCalendarRuleset: ruleset,
      });

      this.http
        .post(`${this.apiUrl}/properties/${propertyId}/calendar`, {
          ...payload,
        })
        .pipe(
          tap(() => {
            this.calendarDaysUpdated$.next({
              propertyId,
              startDate,
              endDate,
            });
          })
        )
        .subscribe(
          () => {
            setTimeout(() => {
              this.eventsService.fire('calendar-ruleset-optimistic-clear', {
                propertyId,
                dates,
              });
            }, 1000);
          },
          () => {
            this.eventsService.fire('calendar-ruleset-optimistic-error', {
              propertyId,
              propertyName: 'Calendar ruleset',
              dates: dates,
              startDate: new Date(startDate),
              endDate: new Date(endDate),
            });
          }
        );
    });
  }

  openRulesetApplyDialog(mode: RuleSetDialogMode): MatDialogRef<RulesetSearchComponent> {
    const dialogRef = this.dialog.open(RulesetSearchComponent, {
      data: { mode },
      width: '100%',
      height: '100%',
      maxWidth: '100%',
      maxHeight: '100%',
      panelClass: 'dialog__no-padding',
      disableClose: true,
    });

    return dialogRef;
  }

  openRulesetDeletePrompt(): MatDialogRef<RulesetDeletePromptComponent> {
    const dialogRef = this.dialog.open(RulesetDeletePromptComponent, {});
    return dialogRef;
  }

  openAddEditRulesetDialog({ rulesetId, mode }: { rulesetId: number | null, mode: RuleSetDialogMode }): MatDialogRef<RulesetFormComponent> {
    const dialogRef = this.dialog.open(RulesetFormComponent, {
      data: { rulesetId: rulesetId ? rulesetId.toString() : null, mode },
      width: '100%',
      height: '100%',
      maxWidth: '100%',
      maxHeight: '100%',
      panelClass: 'dialog__no-padding',
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result: { action: RuleSetDialogAction, data: { uuid: string } | null }) => {
      if ([RuleSetDialogAction.CREATE, RuleSetDialogAction.UPDATE, RuleSetDialogAction.DELETE].includes(result.action)) {
        // TODO: Updated or deleted means we could have changes on other days too
        // Trigger the calendar, refetch rulesets and pricing/availability
        this.rulesetsUpdated$.next();
      }
    });

    return dialogRef;
  }

  // happens on calendar modal, has been moved outside angular zone to avoid blocking the main thread, until the modal is needed.
  public checkForOptInAndOpenModal() {
    this.enabled$
      .pipe(
        filter((enabled) => enabled === false),
        take(1)
      )
      .subscribe(() => {
        this.zone.runOutsideAngular(() => {
          setTimeout(() => {
            this.checkForOptIn()
              .pipe(
                filter((rulesets) => rulesets !== false),
                take(1)
              )
              .subscribe((rulesets) => {
                this.zone.run(() => {
                  this.segmentIoService.track(SegmentEvent.RuleSetsOptInModalAutoTriggered, {});
                  this.openRuleSetsOptInModal(rulesets as ApiSimplePropertyRuleset[]);
                });
              });
          }, 5000);
        });
      });
  }

  public async checkForAirbnbRuleSetsAndOpenModal() {
    const enabled = await firstValueFrom(this.enabled$);

    if (enabled) {
      return false;
    }

    const rulesets = await firstValueFrom(this.getCalendarRulesets('airbnb-official', 'recency').pipe(take(1)));

    if (rulesets.length > 0) {
      this.segmentIoService.track(SegmentEvent.RuleSetsOptInModalActionTriggered, {});
      this.openRuleSetsOptInModal(rulesets as ApiSimplePropertyRuleset[]);
      return true;
    }
    return false;
  }

  public openRuleSetsOptInModal(rulesets: ApiSimplePropertyRuleset[]) {

    if (this.dialog.openDialogs.length === 0) {
      this.dialog.open(RuleSetsOptInModalComponent, {
        panelClass: 'dialog-rulsets-opt-in',
        data: {
          rulesets,
        },
      });
    }
  }

  private mapApiPropertyRulesetToPropertyRuleset(ruleset: ApiPropertyRuleset): PropertyRuleset {
    return {
      uuid: ruleset.id,
      name: ruleset.name,
      color: ruleset.color,
      seasonal_adjustment: ruleset.seasonal_adjustment,
      min_max_stay: {
        min: ruleset.min_nights,
        max: ruleset.max_nights,
        min_nights_by_day: ruleset.min_nights_by_day,
      },
      restricted_check_in_out: {
        check_in: ruleset.check_in_restricted,
        check_out: ruleset.check_out_restricted,
      },
      discounts: {
        length_of_stay: ruleset.length_of_stay,
        last_minute: ruleset.last_minute,
        early_bird: ruleset.early_bird,
      },
    };
  }

  private mapPropertyRulesetToApiPropertyRuleset(ruleset: PropertyRuleset): ApiPropertyRuleset {
    return {
      id: ruleset.uuid,
      name: ruleset.name,
      color: ruleset.color,
      seasonal_adjustment:
        (
          (ruleset.seasonal_adjustment.value === null || ruleset.seasonal_adjustment.value === 0)
          && ruleset.seasonal_adjustment.max_price === null
          && ruleset.seasonal_adjustment.min_price === null
        )
          ? null
          : ruleset.seasonal_adjustment,

      min_nights: ruleset.min_max_stay?.min,
      max_nights: ruleset.min_max_stay?.max,

      min_nights_by_day: ruleset.min_max_stay?.min_nights_by_day?.filter((day) => day.value !== null),
      check_in_restricted: ruleset.restricted_check_in_out?.check_in,
      check_out_restricted: ruleset.restricted_check_in_out?.check_out,

      length_of_stay: ruleset.discounts?.length_of_stay,
      last_minute: ruleset.discounts?.last_minute,
      early_bird: ruleset.discounts?.early_bird,
    };
  }

  private formatDateOrError(date: string): string {
    date = format(date, 'YYYY-MM-DD');

    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(date)) {
      Logger.error(
        '[PropertyRulesetService] (formatDateOrError) Invalid date format. Dates must be in YYYY-MM-DD format'
      );
      return null;
    }

    return date;
  }

  NEW_RULESET_DEFAULT_VALUES: PropertyRuleset = {
    uuid: null,
    name: 'New ruleset',
    color: 0,
    seasonal_adjustment: null,
    min_max_stay: null,
    restricted_check_in_out: null,
    discounts: {
      length_of_stay: null,
      last_minute: null,
      early_bird: null,
    },
  };

  DAYS_OF_WEEK_MAPPING_MIN_NIGHTS_BY_DAY = {
    0: 'SUNDAY',
    1: 'MONDAY',
    2: 'TUESDAY',
    3: 'WEDNESDAY',
    4: 'THURSDAY',
    5: 'FRIDAY',
    6: 'SATURDAY',
  };

  TEST_API_RESPONSE: ApiPropertyRuleset = {
    id: '123',
    name: 'Fake ruleset - Summer season',
    color: 1,
    seasonal_adjustment: {
      value: 30,
      type: 'increase',
      min_price: 100,
      max_price: 1000,
    },
    min_nights: null,
    max_nights: null,
    min_nights_by_day: null,
    check_in_restricted: [2, 3],
    check_out_restricted: [0, 1, 6],
    length_of_stay: [
      {
        id: 31772,
        nights: 3,
        value: -5,
      },
      {
        id: 31773,
        nights: 5,
        value: -10,
      },
    ],
    last_minute: [
      {
        id: 31774,
        days: 1,
        value: -5,
      },
    ],
    early_bird: [
      {
        id: 31775,
        months: 1,
        value: -5,
      },
    ],
  };
}
